import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  Image,
  Text,
  Card,
  CardBody,
  HStack,
  Badge,
  useBreakpointValue,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTable } from 'react-table';

import networkImage from '../../assets/images/network.svg';
import TableSkeleton from './table-skeleton';

const MotionTr = motion(Tr);
const MotionCard = motion(Card);

// Animation variants
const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
  exit: { opacity: 0, y: -10 },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: i * 0.08,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

// Mobile Card View Component
function MobileCardView({ row, columns, customColumn, index }) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const titleColor = useColorModeValue('gray.800', 'white');

  return (
    <MotionCard
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      cursor={customColumn?.onClick ? 'pointer' : 'default'}
      onClick={() => customColumn?.onClick?.(row)}
      _hover={{
        transform: 'translateY(-2px)',
        boxShadow: 'md',
      }}
      transition="all 0.2s"
    >
      <CardBody p={4}>
        <VStack align="stretch" spacing={3}>
          {row.cells.map((cell, cellIndex) => {
            const header = columns[cellIndex]?.Header || '';
            const isFirstColumn = cellIndex === 0;

            return (
              <Flex
                key={cellIndex}
                justify="space-between"
                align={isFirstColumn ? 'flex-start' : 'center'}
                flexDirection={isFirstColumn ? 'column' : 'row'}
              >
                {isFirstColumn ? (
                  <Box>
                    <Text
                      fontWeight="bold"
                      fontSize="md"
                      color={titleColor}
                    >
                      {cell.render('Cell')}
                    </Text>
                  </Box>
                ) : (
                  <>
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      fontWeight="medium"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      {header}
                    </Text>
                    <Box fontSize="sm" textAlign="right">
                      {cell.render('Cell')}
                    </Box>
                  </>
                )}
              </Flex>
            );
          })}
        </VStack>
      </CardBody>
    </MotionCard>
  );
}

// Desktop Table View Component
function DesktopTableView({ headerGroups, rows, prepareRow, getTableProps, getTableBodyProps, customColumn }) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <TableContainer>
      <Table variant="simple" {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()} bg={headerBg}>
              {headerGroup.headers.map((column) => (
                <Th
                  {...column.getHeaderProps()}
                  py={4}
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="gray.500"
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          <AnimatePresence>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <MotionTr
                  {...row.getRowProps()}
                  {...customColumn}
                  custom={i}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={() => customColumn?.onClick?.(row)}
                  cursor={customColumn?.onClick ? 'pointer' : 'default'}
                  _hover={{ bg: hoverBg }}
                  transition="background 0.2s"
                >
                  {row.cells.map((cell) => (
                    <Td
                      {...cell.getCellProps()}
                      py={4}
                      fontSize="sm"
                    >
                      {cell.render('Cell')}
                    </Td>
                  ))}
                </MotionTr>
              );
            })}
          </AnimatePresence>
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function DataTable({
  columns,
  data,
  isLoaded = false,
  customColumn,
  mobileBreakpoint = 'md',
  ...props
}) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  // Use card view on mobile
  const isMobile = useBreakpointValue({ base: true, [mobileBreakpoint]: false });
  const emptyBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <Box w="full" {...props}>
      {/* Loading State */}
      {!isLoaded && <TableSkeleton />}

      {/* Empty State */}
      {isLoaded && data.length === 0 && (
        <VStack
          flexGrow={1}
          py={12}
          align="center"
          justify="center"
          bg={emptyBg}
          borderRadius="xl"
        >
          <Image src={networkImage} w="120px" opacity={0.7} />
          <Text fontSize="sm" color="gray.500" mt={4}>
            No data available
          </Text>
        </VStack>
      )}

      {/* Data View */}
      {isLoaded && data.length > 0 && (
        <>
          {isMobile ? (
            // Mobile Card View
            <VStack spacing={3} align="stretch">
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <MobileCardView
                    key={i}
                    row={row}
                    columns={columns}
                    customColumn={customColumn}
                    index={i}
                  />
                );
              })}
            </VStack>
          ) : (
            // Desktop Table View
            <DesktopTableView
              headerGroups={headerGroups}
              rows={rows}
              prepareRow={prepareRow}
              getTableProps={getTableProps}
              getTableBodyProps={getTableBodyProps}
              customColumn={customColumn}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default DataTable;
